import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Card, CardActions, CardContent } from '@material-ui/core';
import { CardHeaderGradient } from 'modules/core/components/common/card';
import { BACKGROUND } from 'themes/utils/background-utils';
import { useTranslation } from 'react-i18next';
import { DefaultTheme } from 'themes/interfaces/default-theme.interfaces';
import { useSelector } from 'react-redux';
import { feVisualizationType } from 'modules/core/store/selectors';
import { SupportedCompanies } from 'config';

export interface IFeedCardProps {
  className?: string;
  title: string;
  titleIcon?: any;
  headingColor?: BACKGROUND;
  openList?: () => void | null;
  buttonText?: string;
  buttonAction?: () => void | null;
}

const makeClasses = makeStyles<DefaultTheme, { isEX: boolean, }>(theme => {
  return {
    root: {
      background: theme.palette.common.white,
      boxShadow: props => !props.isEX ? '0 31px 53px rgba(0, 0, 0, 0.3), inset 0px 0px 0px 1px #E4E4E4' : 'none',
      marginBottom: theme.spacing(2),
      height: props => props.isEX ? '550px' : 'auto'
    },
    content: {
      ...theme.mixins.fonts.regular.xs,
      height: '100%',
      color: theme.palette.grey['500'],
      '& p:first-of-type': {
        marginTop: 0
      }
    },
    cardTitle: {
      ...theme.mixins.fonts.light.s,
    },
    cardTitleEx: {
      ...theme.mixins.fonts.regular.s,
    },
    cardContent: {
      padding: theme.spacing(3),
      height: '100%'
    },
    actionButton: {
      width: '100%',
    },
    seeModalButton: {
      color: theme.palette.common.white,
      borderColor: theme.palette.common.white,
      '& span:first-of-type': {
        fontSize: '14px'
      },
    },
    buttonWrapper: {
      padding: '8px 24px'
    }
  };
});

export const FeedCard: React.FC<IFeedCardProps> = (props: React.PropsWithChildren<IFeedCardProps>) => {
  const { headingColor, titleIcon: TitleIcon, className, title, children, buttonAction, buttonText } = props;
  const { t } = useTranslation();
  const visualizationType = useSelector(feVisualizationType);
  const isEXCompany = visualizationType === SupportedCompanies.EX;
  const classes = makeClasses({
    isEX: isEXCompany
  });

  return (
    <Card className={clsx(classes.root, className)}>
      <CardHeaderGradient
        color={headingColor}
        fontColor={isEXCompany ? '#302B27' : '#FFFFFF'}
      >
        {TitleIcon && <TitleIcon />}
        <span
          className={isEXCompany ? classes.cardTitleEx : classes.cardTitle}>{title}</span>
      </CardHeaderGradient>
      <CardContent className={classes.cardContent}>
        <div className={classes.content}>{children}</div>

      </CardContent>
      {buttonAction && isEXCompany && <CardActions className={classes.buttonWrapper}>
        <Button
          variant='outlined'
          size='medium'
          onClick={buttonAction}
          className={classes.actionButton}
        >
          {buttonText}
        </Button>
      </CardActions>}
    </Card>
  );
};

FeedCard.defaultProps = {
  headingColor: BACKGROUND.PRIMARY
};
